import React, { useState, useEffect } from 'react';
import {
    Save,
    AlertCircle,
    CheckCircle,
    Mail,
    Clock,
    AlertTriangle,
    Shield,
    ShieldCheck,
    Zap,
    Plus,
    X,
    Loader2,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import { apiClient, NotificationSettings as NotificationSettingsType } from '../services/api';

interface NotificationSettingsProps {
    user: any;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ user }) => {
    const [settings, setSettings] = useState<NotificationSettingsType | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const [licenceEmailInput, setLicenceEmailInput] = useState('');
    const [consentEmailInput, setConsentEmailInput] = useState('');
    const [complianceEmailInput, setComplianceEmailInput] = useState('');
    const [bulkEmailInput, setBulkEmailInput] = useState('');
    const [digestEmailInput, setDigestEmailInput] = useState('');
    const [consentActionDigestEmailInput, setConsentActionDigestEmailInput] = useState('');
    const [dbsEmailInput, setDbsEmailInput] = useState('');

    const [runningManualCheck, setRunningManualCheck] = useState<{
        licence: boolean;
        consent: boolean;
        compliance: boolean;
        digest: boolean;
    }>({ licence: false, consent: false, compliance: false, digest: false });

    const [manualCheckResults, setManualCheckResults] = useState<{
        licence?: { sent: number; failed: number };
        consent?: { sent: number; failed: number };
        compliance?: { sent: number; failed: number };
        digest?: { recipients: string[]; sentAt: string };
    }>({});

    const [collapsedSections, setCollapsedSections] = useState<{
        licence: boolean;
        consent: boolean;
        compliance: boolean;
        bulk: boolean;
        digest: boolean;
        consentActionDigest: boolean;
        dbs: boolean;
    }>({
        licence: true,
        consent: true,
        compliance: true,
        bulk: true,
        digest: true,
        consentActionDigest: true,
        dbs: true
    });

    const [initialSettings, setInitialSettings] = useState<NotificationSettingsType | null>(null);

    const toggleSection = (section: keyof typeof collapsedSections) => {
        setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const hasChanges = () => {
        if (!settings || !initialSettings) return false;
        return JSON.stringify(settings) !== JSON.stringify(initialSettings);
    };

    useEffect(() => { loadSettings(); }, []);

    const loadSettings = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const response = await apiClient.getNotificationSettings();
            setSettings(response.data);
            setInitialSettings(JSON.parse(JSON.stringify(response.data)));
        } catch (err: any) {
            setError(err.message || 'Failed to load notification settings');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!settings) return;
        try {
            setIsSaving(true);
            setError(null);
            setSuccessMessage(null);
            await apiClient.updateNotificationSettings(settings);
            setInitialSettings(JSON.parse(JSON.stringify(settings)));
            setSuccessMessage('Notification settings saved successfully');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err: any) {
            setError(err.message || 'Failed to save notification settings');
        } finally {
            setIsSaving(false);
        }
    };

    const addEmail = async (type: 'licence' | 'consent' | 'compliance' | 'bulk' | 'digest' | 'consentActionDigest' | 'dbs', email: string) => {
        if (!settings) return;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) { setError('Please enter a valid email address'); return; }
        const fieldMap = {
            licence: 'licenceExpiryRecipients',
            consent: 'consentExpiryRecipients',
            compliance: 'complianceRecipients',
            bulk: 'bulkRecipients',
            digest: 'dailyDigestRecipients',
            consentActionDigest: 'consentActionDigestRecipients',
            dbs: 'dbsRecipients'
        } as const;
        const field = fieldMap[type];
        const recipients = settings[field] as string[];
        if (recipients.includes(email)) { setError('This email is already in the list'); return; }
        const updatedSettings = { ...settings, [field]: [...recipients, email] };
        setSettings(updatedSettings);
        switch (type) {
            case 'licence': setLicenceEmailInput(''); break;
            case 'consent': setConsentEmailInput(''); break;
            case 'compliance': setComplianceEmailInput(''); break;
            case 'bulk': setBulkEmailInput(''); break;
            case 'digest': setDigestEmailInput(''); break;
            case 'consentActionDigest': setConsentActionDigestEmailInput(''); break;
            case 'dbs': setDbsEmailInput(''); break;
        }
        setError(null);
        try {
            setIsSaving(true);
            await apiClient.updateNotificationSettings(updatedSettings);
        } catch (err: any) {
            setError(`Failed to save email: ${err.message || 'Unknown error'}`);
            setSettings(settings);
        } finally {
            setIsSaving(false);
        }
    };

    const removeEmail = async (type: 'licence' | 'consent' | 'compliance' | 'bulk' | 'digest' | 'consentActionDigest' | 'dbs', email: string) => {
        if (!settings) return;
        const fieldMap = {
            licence: 'licenceExpiryRecipients',
            consent: 'consentExpiryRecipients',
            compliance: 'complianceRecipients',
            bulk: 'bulkRecipients',
            digest: 'dailyDigestRecipients',
            consentActionDigest: 'consentActionDigestRecipients',
            dbs: 'dbsRecipients'
        } as const;
        const field = fieldMap[type];
        const recipients = settings[field] as string[];
        const updatedSettings = { ...settings, [field]: recipients.filter(e => e !== email) };
        setSettings(updatedSettings);
        try {
            setIsSaving(true);
            await apiClient.updateNotificationSettings(updatedSettings);
        } catch (err: any) {
            setError(`Failed to remove email: ${err.message || 'Unknown error'}`);
            setSettings(settings);
        } finally {
            setIsSaving(false);
        }
    };

    const runManualLicenceCheck = async () => {
        try {
            setRunningManualCheck({ ...runningManualCheck, licence: true });
            setError(null);
            setManualCheckResults({ ...manualCheckResults, licence: undefined });
            const response = await apiClient.triggerLicenceExpiryNotifications();
            setManualCheckResults({ ...manualCheckResults, licence: response.data });
            const { sent, failed } = response.data;
            setSuccessMessage(sent > 0 || failed > 0
                ? `Manual licence check complete: ${sent} notification${sent === 1 ? '' : 's'} sent${failed > 0 ? `, ${failed} failed` : ''}`
                : 'Manual licence check complete: No expiring licences found');
            setTimeout(() => { setSuccessMessage(null); setManualCheckResults({ ...manualCheckResults, licence: undefined }); }, 5000);
        } catch (err: any) {
            setError(`Failed to run manual licence check: ${err.message}`);
        } finally {
            setRunningManualCheck({ ...runningManualCheck, licence: false });
        }
    };

    const runManualConsentCheck = async () => {
        try {
            setRunningManualCheck({ ...runningManualCheck, consent: true });
            setError(null);
            setManualCheckResults({ ...manualCheckResults, consent: undefined });
            const response = await apiClient.triggerConsentExpiryNotifications();
            setManualCheckResults({ ...manualCheckResults, consent: response.data });
            const { sent, failed } = response.data;
            setSuccessMessage(sent > 0 || failed > 0
                ? `Manual consent check complete: ${sent} notification${sent === 1 ? '' : 's'} sent${failed > 0 ? `, ${failed} failed` : ''}`
                : 'Manual consent check complete: No expiring consents found');
            setTimeout(() => { setSuccessMessage(null); setManualCheckResults({ ...manualCheckResults, consent: undefined }); }, 5000);
        } catch (err: any) {
            setError(`Failed to run manual consent check: ${err.message}`);
        } finally {
            setRunningManualCheck({ ...runningManualCheck, consent: false });
        }
    };

    const runManualComplianceCheck = async () => {
        try {
            setRunningManualCheck({ ...runningManualCheck, compliance: true });
            setError(null);
            setManualCheckResults({ ...manualCheckResults, compliance: undefined });
            const response = await apiClient.triggerComplianceCheck();
            setManualCheckResults({ ...manualCheckResults, compliance: response.data });
            const { sent, failed } = response.data;
            setSuccessMessage(sent > 0 || failed > 0
                ? `Manual compliance check complete: ${sent} alert${sent === 1 ? '' : 's'} sent${failed > 0 ? `, ${failed} failed` : ''}`
                : 'Manual compliance check complete: No compliance issues found');
            setTimeout(() => { setSuccessMessage(null); setManualCheckResults({ ...manualCheckResults, compliance: undefined }); }, 5000);
        } catch (err: any) {
            setError(`Failed to run manual compliance check: ${err.message}`);
        } finally {
            setRunningManualCheck({ ...runningManualCheck, compliance: false });
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#69BE28' }} />
                <span className="ml-3 text-gray-600">Loading settings...</span>
            </div>
        );
    }

    if (!settings) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                    <div>
                        <h3 className="text-sm font-medium text-red-800">Failed to Load Settings</h3>
                        <p className="text-sm text-red-700 mt-1">{error}</p>
                    </div>
                </div>
                <button onClick={loadSettings} className="mt-4 px-4 py-2 text-sm text-white rounded-lg hover:opacity-90" style={{ backgroundColor: '#69BE28' }}>
                    Retry
                </button>
            </div>
        );
    }

    const chevronBtn = (section: keyof typeof collapsedSections) => (
        <button
            onClick={() => toggleSection(section)}
            className="p-1 rounded hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
        >
            {collapsedSections[section] ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>
    );

    const toggleSwitch = (checked: boolean, onChange: (v: boolean) => void) => (
        <label className="relative inline-flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
            <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
        </label>
    );

    const recipientInput = (
        value: string,
        onChange: (v: string) => void,
        onAdd: () => void,
        disabled: boolean,
        type: 'licence' | 'consent' | 'compliance' | 'bulk' | 'digest' | 'consentActionDigest' | 'dbs'
    ) => (
        <div className="flex gap-2">
            <input
                type="email"
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAdd(); } }}
                placeholder="Add recipient email"
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
            />
            <button
                onClick={onAdd}
                disabled={!value || disabled}
                className="px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#69BE28' }}
            >
                <Plus className="w-4 h-4" />
            </button>
        </div>
    );

    const recipientPills = (
        emails: string[],
        onRemove: (email: string) => void,
        disabled: boolean
    ) => emails.length > 0 ? (
        <div className="flex flex-wrap gap-2 mt-2">
            {emails.map((email) => (
                <span key={email} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                    <Mail className="w-3 h-3 mr-1" />
                    {email}
                    <button onClick={() => onRemove(email)} disabled={disabled} className="ml-2 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed">
                        <X className="w-3 h-3" />
                    </button>
                </span>
            ))}
        </div>
    ) : null;

    const manualCheckArea = (
        onRun: () => void,
        isRunning: boolean,
        disabled: boolean,
        result: { sent: number; failed: number } | undefined,
        label: string,
        icon: React.ReactNode
    ) => (
        <div className="pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-2">Runs immediately and sends real alerts to the recipients above.</p>
            <button
                onClick={onRun}
                disabled={isRunning || disabled}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity disabled:cursor-not-allowed"
                style={{ backgroundColor: '#69BE28' }}
            >
                {isRunning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running...</> : <>{icon}{label}</>}
            </button>
            {result && (
                <p className="mt-2 text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 inline text-green-600 mr-1" />
                    {result.sent > 0
                        ? `Sent ${result.sent} notification${result.sent === 1 ? '' : 's'}.`
                        : 'No matches found.'}
                </p>
            )}
        </div>
    );

    return (
        <div className="space-y-4">
            {successMessage && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                    <p className="text-sm text-green-800">{successMessage}</p>
                </div>
            )}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
                    <p className="text-sm text-red-800">{error}</p>
                </div>
            )}

            <div className="divide-y divide-gray-100">

                {/* Licence Expiry Alerts */}
                <div className="pb-5">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1 cursor-pointer" onClick={() => toggleSection('licence')}>
                            <AlertTriangle className="w-5 h-5 mr-3 flex-shrink-0" style={{ color: '#69BE28' }} />
                            <div>
                                <h3 className="text-sm font-medium text-gray-900">Licence Expiry Alerts</h3>
                                <p className="text-xs text-gray-500">Automated alerts for expiring driver licences</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {toggleSwitch(settings.licenceExpiryEnabled, (v) => setSettings({ ...settings, licenceExpiryEnabled: v }))}
                            {chevronBtn('licence')}
                        </div>
                    </div>
                    {!collapsedSections.licence && (
                        <div className={`mt-4 space-y-4 ${!settings.licenceExpiryEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                            <p className="text-xs text-gray-500">
                                Checks all drivers daily at 7:00 AM UTC. Alerts go to the recipients below (not to drivers). One email per threshold groups all affected drivers.
                            </p>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Alert when licence expires in (days)</label>
                                <div className="flex gap-4">
                                    {[30, 14, 7, 1].map((days) => (
                                        <label key={days} className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={settings.licenceExpiryDays.includes(days)}
                                                disabled={!settings.licenceExpiryEnabled}
                                                onChange={(e) => {
                                                    const newDays = e.target.checked
                                                        ? [...settings.licenceExpiryDays, days].sort((a, b) => b - a)
                                                        : settings.licenceExpiryDays.filter(d => d !== days);
                                                    setSettings({ ...settings, licenceExpiryDays: newDays });
                                                }}
                                                className="w-4 h-4 rounded border-gray-300 accent-[#69BE28] disabled:opacity-50 disabled:cursor-not-allowed"
                                            />
                                            <span className="ml-2 text-sm text-gray-700">{days} days</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email Recipients</label>
                                {recipientInput(licenceEmailInput, setLicenceEmailInput, () => addEmail('licence', licenceEmailInput), !settings.licenceExpiryEnabled, 'licence')}
                                {licenceEmailInput && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(licenceEmailInput) && (
                                    <p className="text-xs text-red-600 mt-1">Please enter a valid email address</p>
                                )}
                                {recipientPills(settings.licenceExpiryRecipients, (e) => removeEmail('licence', e), !settings.licenceExpiryEnabled)}
                            </div>
                            {settings.licenceExpiryRecipients.length > 0 && manualCheckArea(
                                runManualLicenceCheck,
                                runningManualCheck.licence,
                                !settings.licenceExpiryEnabled,
                                manualCheckResults.licence,
                                'Run Manual Licence Check',
                                <AlertTriangle className="w-4 h-4 mr-2" />
                            )}
                        </div>
                    )}
                </div>

                {/* Consent Expiry Alerts */}
                <div className="py-5">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1 cursor-pointer" onClick={() => toggleSection('consent')}>
                            <Shield className="w-5 h-5 mr-3 flex-shrink-0" style={{ color: '#69BE28' }} />
                            <div>
                                <h3 className="text-sm font-medium text-gray-900">Consent Expiry Alerts</h3>
                                <p className="text-xs text-gray-500">Automated alerts for expiring D906 consents</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {toggleSwitch(settings.consentExpiryEnabled, (v) => setSettings({ ...settings, consentExpiryEnabled: v }))}
                            {chevronBtn('consent')}
                        </div>
                    </div>
                    {!collapsedSections.consent && (
                        <div className={`mt-4 space-y-4 ${!settings.consentExpiryEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                            <p className="text-xs text-gray-500">
                                Checks D906 consent expiry dates daily at 7:00 AM UTC. Alerts sent to recipients when consent is expiring soon.
                            </p>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Alert when consent expires in (days)</label>
                                <div className="flex gap-4">
                                    {[30, 14, 7].map((days) => (
                                        <label key={days} className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={settings.consentExpiryDays.includes(days)}
                                                disabled={!settings.consentExpiryEnabled}
                                                onChange={(e) => {
                                                    const newDays = e.target.checked
                                                        ? [...settings.consentExpiryDays, days].sort((a, b) => b - a)
                                                        : settings.consentExpiryDays.filter(d => d !== days);
                                                    setSettings({ ...settings, consentExpiryDays: newDays });
                                                }}
                                                className="w-4 h-4 rounded border-gray-300 accent-[#69BE28] disabled:opacity-50 disabled:cursor-not-allowed"
                                            />
                                            <span className="ml-2 text-sm text-gray-700">{days} days</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email Recipients</label>
                                {recipientInput(consentEmailInput, setConsentEmailInput, () => addEmail('consent', consentEmailInput), !settings.consentExpiryEnabled, 'consent')}
                                {recipientPills(settings.consentExpiryRecipients, (e) => removeEmail('consent', e), !settings.consentExpiryEnabled)}
                            </div>
                            {settings.consentExpiryRecipients.length > 0 && manualCheckArea(
                                runManualConsentCheck,
                                runningManualCheck.consent,
                                !settings.consentExpiryEnabled,
                                manualCheckResults.consent,
                                'Run Manual Consent Check',
                                <Shield className="w-4 h-4 mr-2" />
                            )}
                        </div>
                    )}
                </div>

                {/* Compliance Issue Alerts */}
                <div className="py-5">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1 cursor-pointer" onClick={() => toggleSection('compliance')}>
                            <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" style={{ color: '#69BE28' }} />
                            <div>
                                <h3 className="text-sm font-medium text-gray-900">Compliance Issue Alerts</h3>
                                <p className="text-xs text-gray-500">Daily scheduled alerts for drivers with compliance issues</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {toggleSwitch(settings.complianceAlertsEnabled, (v) => setSettings({ ...settings, complianceAlertsEnabled: v }))}
                            {chevronBtn('compliance')}
                        </div>
                    </div>
                    {!collapsedSections.compliance && (
                        <div className={`mt-4 space-y-4 ${!settings.complianceAlertsEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                            <p className="text-xs text-gray-500">
                                One consolidated email per day at your chosen time. Filter by severity: all issues, medium/high, or high only.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Alert Severity</label>
                                    <select
                                        value={settings.complianceSeverity}
                                        disabled={!settings.complianceAlertsEnabled}
                                        onChange={(e) => setSettings({ ...settings, complianceSeverity: e.target.value as any })}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                                    >
                                        <option value="all">All Issues</option>
                                        <option value="medium_high">Medium &amp; High Priority</option>
                                        <option value="high_only">High Priority Only</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Daily Check Time (UTC)</label>
                                    <input
                                        type="time"
                                        value={settings.complianceCheckTime}
                                        disabled={!settings.complianceAlertsEnabled}
                                        onChange={(e) => setSettings({ ...settings, complianceCheckTime: e.target.value })}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Default: 07:30 UTC.</p>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email Recipients</label>
                                {recipientInput(complianceEmailInput, setComplianceEmailInput, () => addEmail('compliance', complianceEmailInput), !settings.complianceAlertsEnabled, 'compliance')}
                                {recipientPills(settings.complianceRecipients, (e) => removeEmail('compliance', e), !settings.complianceAlertsEnabled)}
                            </div>
                            {settings.complianceRecipients.length > 0 && manualCheckArea(
                                runManualComplianceCheck,
                                runningManualCheck.compliance,
                                !settings.complianceAlertsEnabled,
                                manualCheckResults.compliance,
                                'Run Manual Compliance Check',
                                <AlertCircle className="w-4 h-4 mr-2" />
                            )}
                        </div>
                    )}
                </div>

                {/* Bulk Operation Alerts */}
                <div className="py-5">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1 cursor-pointer" onClick={() => toggleSection('bulk')}>
                            <Zap className="w-5 h-5 mr-3 flex-shrink-0" style={{ color: '#69BE28' }} />
                            <div>
                                <h3 className="text-sm font-medium text-gray-900">Bulk Operation Alerts</h3>
                                <p className="text-xs text-gray-500">Status updates for bulk driver imports and licence checks</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {toggleSwitch(settings.bulkAlertsEnabled, (v) => setSettings({ ...settings, bulkAlertsEnabled: v }))}
                            {chevronBtn('bulk')}
                        </div>
                    </div>
                    {!collapsedSections.bulk && (
                        <div className={`mt-4 space-y-4 ${!settings.bulkAlertsEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                            <p className="text-xs text-gray-500">
                                Status updates when bulk driver imports or licence checks complete. Includes success/failure counts and error details.
                            </p>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email Recipients</label>
                                {recipientInput(bulkEmailInput, setBulkEmailInput, () => addEmail('bulk', bulkEmailInput), !settings.bulkAlertsEnabled, 'bulk')}
                                {recipientPills(settings.bulkRecipients, (e) => removeEmail('bulk', e), !settings.bulkAlertsEnabled)}
                            </div>
                        </div>
                    )}
                </div>

                {/* Daily Digest Email */}
                <div className="py-5">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1 cursor-pointer" onClick={() => toggleSection('digest')}>
                            <Clock className="w-5 h-5 mr-3 flex-shrink-0" style={{ color: '#69BE28' }} />
                            <div>
                                <h3 className="text-sm font-medium text-gray-900">Daily Digest Email</h3>
                                <p className="text-xs text-gray-500">Scheduled summary of all compliance activity</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {toggleSwitch(settings.dailyDigestEnabled, (v) => setSettings({ ...settings, dailyDigestEnabled: v }))}
                            {chevronBtn('digest')}
                        </div>
                    </div>
                    {!collapsedSections.digest && (
                        <div className={`mt-4 space-y-4 ${!settings.dailyDigestEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                            <p className="text-xs text-gray-500">
                                One consolidated email at your chosen time, covering selected compliance activity. Choose daily or a longer interval.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Send Time (UTC)</label>
                                    <input
                                        type="time"
                                        value={settings.dailyDigestTime}
                                        disabled={!settings.dailyDigestEnabled}
                                        onChange={(e) => setSettings({ ...settings, dailyDigestTime: e.target.value })}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Frequency</label>
                                    <select
                                        value={settings.dailyDigestFrequency}
                                        disabled={!settings.dailyDigestEnabled}
                                        onChange={(e) => setSettings({ ...settings, dailyDigestFrequency: e.target.value as any })}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                                    >
                                        <option value="daily">Daily</option>
                                        <option value="7days">Every 7 Days</option>
                                        <option value="14days">Every 14 Days</option>
                                        <option value="30days">Every 30 Days</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Include in Digest</label>
                                <div className="space-y-2">
                                    {[
                                        { key: 'dailyDigestIncludeLicenceExpiry', label: 'Licence Expiry Warnings' },
                                        { key: 'dailyDigestIncludeConsentExpiry', label: 'Consent Expiry Warnings' },
                                        { key: 'dailyDigestIncludeCompliance', label: 'Compliance Issues' },
                                        { key: 'dailyDigestIncludeBulkOperations', label: 'Bulk Operation Results' },
                                        { key: 'dailyDigestIncludeDbs', label: 'DBS Check Activity' },
                                    ].map(({ key, label }) => (
                                        <label key={key} className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={(settings as any)[key] || false}
                                                disabled={!settings.dailyDigestEnabled}
                                                onChange={(e) => setSettings({ ...settings, [key]: e.target.checked })}
                                                className="w-4 h-4 rounded border-gray-300 accent-[#69BE28] disabled:opacity-50 disabled:cursor-not-allowed"
                                            />
                                            <span className="ml-2 text-sm text-gray-700">{label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email Recipients</label>
                                {recipientInput(digestEmailInput, setDigestEmailInput, () => addEmail('digest', digestEmailInput), !settings.dailyDigestEnabled, 'digest')}
                                {recipientPills(settings.dailyDigestRecipients, (e) => removeEmail('digest', e), !settings.dailyDigestEnabled)}
                            </div>
                        </div>
                    )}
                </div>

                {/* DBS Check Alerts */}
                <div className="py-5">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1 cursor-pointer" onClick={() => toggleSection('dbs')}>
                            <ShieldCheck className="w-5 h-5 mr-3 flex-shrink-0" style={{ color: '#69BE28' }} />
                            <div>
                                <h3 className="text-sm font-medium text-gray-900">DBS Check Alerts</h3>
                                <p className="text-xs text-gray-500">Notify admins when DBS checks require action or are complete</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {toggleSwitch(settings.dbsAlertsEnabled, (v) => setSettings({ ...settings, dbsAlertsEnabled: v }))}
                            {chevronBtn('dbs')}
                        </div>
                    </div>
                    {!collapsedSections.dbs && (
                        <div className={`mt-4 space-y-4 ${!settings.dbsAlertsEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                            <p className="text-xs text-gray-500">
                                Real-time alerts triggered by DBS check status changes. Sent immediately when the selected events occur.
                            </p>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Alert on these events</label>
                                <div className="space-y-2">
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={settings.dbsAlertIdentityComplete}
                                            disabled={!settings.dbsAlertsEnabled}
                                            onChange={(e) => setSettings({ ...settings, dbsAlertIdentityComplete: e.target.checked })}
                                            className="w-4 h-4 rounded border-gray-300 accent-[#69BE28] disabled:opacity-50 disabled:cursor-not-allowed"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">Identity check complete - Admin action required to initiate DBS submission</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={settings.dbsAlertResultAvailable}
                                            disabled={!settings.dbsAlertsEnabled}
                                            onChange={(e) => setSettings({ ...settings, dbsAlertResultAvailable: e.target.checked })}
                                            className="w-4 h-4 rounded border-gray-300 accent-[#69BE28] disabled:opacity-50 disabled:cursor-not-allowed"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">DBS check result available (Clear or Not Clear)</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="checkbox"
                                            checked={settings.dbsAlertRejected}
                                            disabled={!settings.dbsAlertsEnabled}
                                            onChange={(e) => setSettings({ ...settings, dbsAlertRejected: e.target.checked })}
                                            className="w-4 h-4 rounded border-gray-300 accent-[#69BE28] disabled:opacity-50 disabled:cursor-not-allowed"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">DBS check rejected or withdrawn: urgent attention required</span>
                                    </label>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email Recipients</label>
                                {recipientInput(dbsEmailInput, setDbsEmailInput, () => addEmail('dbs', dbsEmailInput), !settings.dbsAlertsEnabled, 'dbs')}
                                {dbsEmailInput && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dbsEmailInput) && (
                                    <p className="text-xs text-red-600 mt-1">Please enter a valid email address</p>
                                )}
                                {recipientPills(settings.dbsRecipients, (e) => removeEmail('dbs', e), !settings.dbsAlertsEnabled)}
                            </div>
                        </div>
                    )}
                </div>

                {/* Consent Action Digest */}
                <div className="pt-5">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1 cursor-pointer" onClick={() => toggleSection('consentActionDigest')}>
                            <Mail className="w-5 h-5 mr-3 flex-shrink-0" style={{ color: '#69BE28' }} />
                            <div>
                                <h3 className="text-sm font-medium text-gray-900">Consent Action Digest</h3>
                                <p className="text-xs text-gray-500">Daily summary of driver consent approvals and declines</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {toggleSwitch(settings.consentActionDigestEnabled, (v) => setSettings({ ...settings, consentActionDigestEnabled: v }))}
                            {chevronBtn('consentActionDigest')}
                        </div>
                    </div>
                    {!collapsedSections.consentActionDigest && (
                        <div className={`mt-4 space-y-4 ${!settings.consentActionDigestEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                            <p className="text-xs text-gray-500">
                                One consolidated daily email with all consent actions (approvals, declines, withdrawals) from the previous day. Drivers still receive their own confirmation emails immediately.
                            </p>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Digest Time (UTC)</label>
                                <input
                                    type="time"
                                    value={settings.consentActionDigestTime}
                                    onChange={(e) => setSettings({ ...settings, consentActionDigestTime: e.target.value })}
                                    disabled={!settings.consentActionDigestEnabled}
                                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                                />
                                <p className="text-xs text-gray-500 mt-1">Default: 09:00 UTC.</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email Recipients</label>
                                {recipientInput(consentActionDigestEmailInput, setConsentActionDigestEmailInput, () => addEmail('consentActionDigest', consentActionDigestEmailInput), !settings.consentActionDigestEnabled, 'consentActionDigest')}
                                {recipientPills(settings.consentActionDigestRecipients, (e) => removeEmail('consentActionDigest', e), !settings.consentActionDigestEnabled)}
                            </div>
                        </div>
                    )}
                </div>

            </div>

            {/* Save Row */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-400">Recipient emails save automatically. Toggle and schedule changes require saving.</p>
                <button
                    onClick={handleSave}
                    disabled={isSaving || !hasChanges()}
                    className="inline-flex items-center px-5 py-2 text-sm text-white font-medium rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                    style={{ backgroundColor: '#69BE28' }}
                >
                    {isSaving ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
                    ) : (
                        <><Save className="w-4 h-4 mr-2" />Save Settings</>
                    )}
                </button>
            </div>
        </div>
    );
};

export default NotificationSettings;
